"use client";

import {
  Camera,
  Eye,
  SwitchCamera,
  Download,
  FileText,
  Images,
  Loader2,
  Plus,
  Upload,
  X,
} from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { compressImageFile } from "../../../utils/imageCompression";
import {
  markStableUploadFile,
  stableUploadFile,
} from "../../../utils/stableUploadFile";
import { Button } from "../../ui/button.jsx";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../ui/dialog.jsx";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../ui/dropdown-menu.jsx";

interface ImageCapturePickerProps {
  /** Label shown on the trigger button (e.g. "Add Images", "Add Documents"). */
  buttonText?: string;
  /** Accept string passed to the underlying file input(s). */
  accept?: string;
  /** Whether multiple files can be chosen from the gallery/file picker at once. */
  multiple?: boolean;
  disabled?: boolean;
  isUploading?: boolean;
  /**
   * Whether to offer a "Take Photo" option that opens the in-page
   * camera. Only meaningful when `accept` targets images â€” automatically
   * ignored otherwise.
   */
  allowCamera?: boolean;
  /** Called with the final, user-confirmed list of files after the review step. */
  onConfirm: (files: File[]) => void;
  variant?: React.ComponentProps<typeof Button>["variant"];
  size?: React.ComponentProps<typeof Button>["size"];
  className?: string;
}

interface PendingFile {
  id: string;
  file: File;
  previewUrl: string;
}

interface ZoomOptions {
  min: number;
  max: number;
  step: number;
  hardware: boolean;
}
const isPdfFile = (file: File) =>
  file.type === "application/pdf" ||
  ((!file.type || file.type === "application/octet-stream") &&
    /\.pdf$/i.test(file.name));
const DIGITAL_ZOOM: ZoomOptions = {
  min: 1,
  max: 4,
  step: 0.1,
  hardware: false,
};
type ZoomCapabilities = MediaTrackCapabilities & {
  zoom?: { min: number; max: number; step?: number };
};

/** Shared picker: preserve device files before review and release previews after use. */
export function ImageCapturePicker({
  buttonText = "Add Images",
  accept = "image/*",
  multiple = true,
  disabled = false,
  isUploading = false,
  allowCamera = true,
  onConfirm,
  variant = "outline",
  size = "sm",
  className,
}: ImageCapturePickerProps) {
  const galleryInputRef = useRef<HTMLInputElement | null>(null);
  const cameraInputRef = useRef<HTMLInputElement | null>(null);
  const [pendingFiles, setPendingFiles] = useState<PendingFile[]>([]);
  const [reviewOpen, setReviewOpen] = useState(false);

  const isImageAccept = accept
    .split(",")
    .some((type) =>
      /^(image\/|\.(jpe?g|png|webp|heic|heif|gif|bmp)$)/i.test(type.trim()),
    );
  const showCameraOption = allowCamera && isImageAccept;

  const pendingRef = useRef<PendingFile[]>([]);
  const generation = useRef(0);
  const [preparing, setPreparing] = useState(false);
  const [selectionError, setSelectionError] = useState("");
  const [cameraOpen, setCameraOpen] = useState(false);
  const [cameraError, setCameraError] = useState("");
  const [cameraReady, setCameraReady] = useState(false);
  const [capturing, setCapturing] = useState(false);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [cameras, setCameras] = useState<MediaDeviceInfo[]>([]);
  const [selectedCamera, setSelectedCamera] = useState("");
  const [activeCamera, setActiveCamera] = useState("");
  const [facingMode, setFacingMode] = useState<"environment" | "user">(
    "environment",
  );
  const [canSwitchFacing, setCanSwitchFacing] = useState(false);
  const [zoomOptions, setZoomOptions] = useState<ZoomOptions>(DIGITAL_ZOOM);
  const [zoom, setZoom] = useState(1);
  const [zoomBusy, setZoomBusy] = useState(false);
  const [zoomError, setZoomError] = useState("");
  const zoomGeneration = useRef(0);
  const returnToReview = useRef(false);
  const [viewingFile, setViewingFile] = useState<PendingFile | null>(null);
  const [filePreview, setFilePreview] = useState({ id: "", url: "" });

  useEffect(() => {
    if (!viewingFile) return;
    const file = viewingFile.file;
    // Keep document previews in the browser's PDF viewer, never an HTML frame.
    const source = isPdfFile(file)
      ? new Blob([file], { type: "application/pdf" })
      : file;
    const url = URL.createObjectURL(source);
    setFilePreview({ id: viewingFile.id, url });
    return () => URL.revokeObjectURL(url);
  }, [viewingFile]);

  const stopCamera = useCallback(() => {
    zoomGeneration.current++;
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
    if (videoRef.current) videoRef.current.srcObject = null;
  }, []);

  useEffect(() => {
    if (!cameraOpen) return;
    let cancelled = false;
    setCameraReady(false);
    setCameraError("");
    setActiveCamera("");
    setZoomOptions(DIGITAL_ZOOM);
    setZoom(1);
    setZoomBusy(false);
    setZoomError("");
    const start = async () => {
      try {
        if (!navigator.mediaDevices?.getUserMedia)
          throw new Error(
            "Camera preview is unavailable. Choose a photo or open the device camera.",
          );
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: false,
          video: {
            ...(selectedCamera
              ? { deviceId: { exact: selectedCamera } }
              : { facingMode: { ideal: facingMode } }),
            width: { ideal: 1280, max: 1280 },
            height: { ideal: 720, max: 720 },
          },
        });
        if (cancelled) {
          stream.getTracks().forEach((track) => track.stop());
          return;
        }
        streamRef.current = stream;
        const track = stream.getVideoTracks?.()[0];
        const settings = track?.getSettings?.();
        setActiveCamera(settings?.deviceId || selectedCamera);
        try {
          const capabilities = track?.getCapabilities?.() as
            ZoomCapabilities | undefined;
          setCanSwitchFacing(
            Boolean(
              capabilities?.facingMode?.includes("user") &&
              capabilities.facingMode.includes("environment"),
            ),
          );
          const range = capabilities?.zoom;
          if (
            track?.applyConstraints &&
            range &&
            Number.isFinite(range.min) &&
            Number.isFinite(range.max) &&
            range.max > Math.max(1, range.min)
          ) {
            const options = {
              min: Math.max(1, range.min),
              max: range.max,
              step: range.step && range.step > 0 ? range.step : 0.1,
              hardware: true,
            };
            setZoomOptions(options);
            const initialZoom =
              (settings as MediaTrackSettings & { zoom?: number })?.zoom ??
              options.min;
            setZoom(Math.max(options.min, Math.min(options.max, initialZoom)));
          }
        } catch {
          // Some browsers expose the camera but not its optional capabilities.
          setCanSwitchFacing(false);
        }
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play();
        }
        try {
          const devices = await navigator.mediaDevices.enumerateDevices?.();
          if (!cancelled && streamRef.current === stream && devices) {
            setCameras(
              devices.filter(
                (device) => device.kind === "videoinput" && device.deviceId,
              ),
            );
          }
        } catch {
          // Camera capture remains available when device enumeration is blocked.
        }
      } catch (error) {
        if (!cancelled) {
          stopCamera();
          setCameraError(
            error instanceof Error && error.name === "NotAllowedError"
              ? "Camera permission is blocked. Allow camera access in your browser settings, or choose a photo."
              : "Cannot start the camera. Close other camera apps and try again, or choose a photo.",
          );
        }
      }
    };
    start();
    return () => {
      cancelled = true;
      stopCamera();
    };
  }, [cameraOpen, selectedCamera, facingMode, stopCamera]);

  const openCamera = (fromReview = false) => {
    returnToReview.current = fromReview;
    setReviewOpen(false);
    setCameraOpen(true);
  };

  const closeCamera = () => {
    stopCamera();
    setCameraReady(false);
    setCameraOpen(false);
    if (returnToReview.current) setReviewOpen(true);
    returnToReview.current = false;
  };

  const switchCamera = () => {
    if (cameras.length > 1) {
      const currentIndex = cameras.findIndex(
        (camera) => camera.deviceId === activeCamera,
      );
      setSelectedCamera(cameras[(currentIndex + 1) % cameras.length].deviceId);
    } else {
      setSelectedCamera("");
      setFacingMode((current) =>
        current === "environment" ? "user" : "environment",
      );
    }
  };

  const changeZoom = async (value: number) => {
    const nextZoom = Math.max(
      zoomOptions.min,
      Math.min(zoomOptions.max, value),
    );
    if (!Number.isFinite(nextZoom)) return;
    if (!zoomOptions.hardware) {
      setZoom(nextZoom);
      return;
    }
    const stream = streamRef.current;
    const track = stream?.getVideoTracks?.()[0];
    if (!track || zoomBusy) return;
    const run = ++zoomGeneration.current;
    setZoomBusy(true);
    setZoomError("");
    try {
      await track.applyConstraints({
        advanced: [
          { zoom: nextZoom } as MediaTrackConstraintSet & { zoom: number },
        ],
      });
      if (streamRef.current === stream && zoomGeneration.current === run)
        setZoom(nextZoom);
    } catch {
      if (streamRef.current === stream && zoomGeneration.current === run)
        setZoomError(
          "This camera could not change zoom. Try a smaller zoom level or switch cameras.",
        );
    } finally {
      if (streamRef.current === stream && zoomGeneration.current === run)
        setZoomBusy(false);
    }
  };

  useEffect(
    () => () => {
      generation.current++;
      pendingRef.current.forEach((p) => {
        if (p.previewUrl) URL.revokeObjectURL(p.previewUrl);
      });
      stopCamera();
    },
    [stopCamera],
  );

  const replacePending = (files: PendingFile[]) => {
    pendingRef.current = files;
    setPendingFiles(files);
  };

  const addFiles = async (files: File[]) => {
    if (!files.length) return;
    const run = ++generation.current;
    setSelectionError("");
    setPreparing(true);
    setReviewOpen(true);
    const errors: string[] = [];
    for (const selected of files) {
      if (generation.current !== run) return;
      try {
        // Read provider-backed files while the native input still owns them.
        const stable = await stableUploadFile(selected);
        if (generation.current !== run) return;
        const file = await compressImageFile(stable);
        if (generation.current !== run) return;
        let previewUrl = "";
        if (file.type.startsWith("image/")) {
          const thumbnail = await compressImageFile(file, {
            maxWidth: 192,
            maxHeight: 192,
            quality: 0.6,
            skipIfSmallerThan: 0,
            allowLarger: true,
          });
          if (generation.current !== run) return;
          // Never decode a full-size original just to display a thumbnail.
          if (thumbnail !== file) previewUrl = URL.createObjectURL(thumbnail);
        }
        if (!multiple) {
          pendingRef.current.forEach((pending) => {
            if (pending.previewUrl) URL.revokeObjectURL(pending.previewUrl);
          });
        }
        replacePending([
          ...(multiple ? pendingRef.current : []),
          { id: `${Date.now()}-${Math.random()}`, file, previewUrl },
        ]);
      } catch (error) {
        errors.push(
          `${selected.name}: ${error instanceof Error ? error.message : "Cannot read this file. Select it again."}`,
        );
      }
    }
    if (generation.current === run) {
      setPreparing(false);
      setSelectionError(errors.join(" "));
    }
  };

  const handleInputChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target;
    try {
      await addFiles(Array.from(input.files || []));
    } finally {
      input.value = "";
    }
  };

  const removePendingFile = (id: string) => {
    if (viewingFile?.id === id) setViewingFile(null);
    const target = pendingRef.current.find((p) => p.id === id);
    if (target?.previewUrl) URL.revokeObjectURL(target.previewUrl);
    replacePending(pendingRef.current.filter((p) => p.id !== id));
  };

  const handleCancel = () => {
    setViewingFile(null);
    generation.current++;
    pendingRef.current.forEach((p) => {
      if (p.previewUrl) URL.revokeObjectURL(p.previewUrl);
    });
    replacePending([]);
    setPreparing(false);
    setSelectionError("");
    setReviewOpen(false);
  };

  const handleConfirmUpload = () => {
    if (preparing || !pendingRef.current.length) return;
    const files = pendingRef.current.map((p) => p.file);
    handleCancel();
    onConfirm(files);
  };

  const capturePhoto = async () => {
    const video = videoRef.current;
    const capturedStream = streamRef.current;
    if (!video?.videoWidth || !capturedStream || capturing || zoomBusy) return;
    setCapturing(true);
    const canvas = document.createElement("canvas");
    try {
      const scale = Math.min(
        1280 / video.videoWidth,
        1280 / video.videoHeight,
        1,
      );
      canvas.width = Math.round(video.videoWidth * scale);
      canvas.height = Math.round(video.videoHeight * scale);
      const context = canvas.getContext("2d");
      if (!context) throw new Error("Cannot prepare photo");
      // Hardware zoom is already present in the stream. Digital zoom must
      // crop the captured image as well as the preview.
      const captureZoom = zoomOptions.hardware ? 1 : zoom;
      const cropWidth = video.videoWidth / captureZoom;
      const cropHeight = video.videoHeight / captureZoom;
      context.drawImage(
        video,
        (video.videoWidth - cropWidth) / 2,
        (video.videoHeight - cropHeight) / 2,
        cropWidth,
        cropHeight,
        0,
        0,
        canvas.width,
        canvas.height,
      );

      const blob = await new Promise<Blob>((resolve, reject) =>
        canvas.toBlob(
          (value) =>
            value ? resolve(value) : reject(new Error("Cannot capture photo")),
          "image/jpeg",
          0.8,
        ),
      );
      // Closing the camera during capture must not reopen the review dialog.
      if (streamRef.current !== capturedStream) return;
      stopCamera();
      setCameraOpen(false);
      returnToReview.current = false;
      await addFiles([
        markStableUploadFile(
          new File([blob], `photo-${Date.now()}.jpg`, { type: "image/jpeg" }),
        ),
      ]);
    } catch {
      if (streamRef.current === capturedStream)
        setCameraError(
          "Cannot capture this photo. Try again or choose a photo from your gallery.",
        );
    } finally {
      canvas.width = 0;
      canvas.height = 0;
      setCapturing(false);
    }
  };

  const triggerButton = (
    <Button
      type="button"
      variant={variant}
      size={size}
      className={className}
      disabled={disabled || isUploading || preparing}
      onClick={
        showCameraOption ? undefined : () => galleryInputRef.current?.click()
      }
    >
      {isUploading ? (
        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
      ) : (
        <Plus className="w-4 h-4 mr-2" />
      )}
      {isUploading ? "Uploading..." : buttonText}
    </Button>
  );

  return (
    <>
      <input
        ref={galleryInputRef}
        aria-label="Choose files"
        type="file"
        accept={accept}
        multiple={multiple}
        className="hidden"
        onChange={handleInputChange}
      />
      {showCameraOption && (
        <input
          ref={cameraInputRef}
          aria-label="Take photo using device camera"
          type="file"
          accept="image/*"
          capture={facingMode}
          className="hidden"
          onChange={handleInputChange}
        />
      )}

      {showCameraOption ? (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>{triggerButton}</DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => openCamera()}>
              <Camera className="w-4 h-4" />
              Take Photo
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => galleryInputRef.current?.click()}>
              <Images className="w-4 h-4" />
              Choose from Gallery
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ) : (
        triggerButton
      )}

      <Dialog
        open={cameraOpen}
        onOpenChange={(open) => {
          if (!open) closeCamera();
        }}
      >
        <DialogContent className="max-h-[calc(100dvh-2rem)] overflow-y-auto p-4 sm:max-w-lg sm:p-6">
          <DialogHeader>
            <DialogTitle>Take a photo</DialogTitle>
            <DialogDescription>
              Allow camera access, then capture your photo for review.
            </DialogDescription>
          </DialogHeader>
          {cameraError ? (
            <p role="alert" className="text-sm text-destructive">
              {cameraError}
            </p>
          ) : null}
          {(cameras.length > 1 || canSwitchFacing) && (
            <div className="flex flex-wrap items-center gap-2">
              {cameras.length > 1 && (
                <select
                  aria-label="Camera"
                  value={activeCamera || selectedCamera}
                  disabled={capturing || zoomBusy}
                  onChange={(event) => setSelectedCamera(event.target.value)}
                  className="h-10 min-w-0 flex-1 rounded-md border border-border bg-background px-3 text-sm text-foreground"
                >
                  <option value="" disabled>
                    Choose a camera
                  </option>
                  {cameras.map((camera, index) => (
                    <option key={camera.deviceId} value={camera.deviceId}>
                      {camera.label || `Camera ${index + 1}`}
                    </option>
                  ))}
                </select>
              )}
              <Button
                type="button"
                variant="outline"
                onClick={switchCamera}
                disabled={capturing || zoomBusy}
              >
                <SwitchCamera className="h-4 w-4" /> Switch camera
              </Button>
            </div>
          )}
          <div className="overflow-hidden rounded-lg bg-black">
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              className="block max-h-[45vh] w-full object-contain"
              style={{ transform: `scale(${zoomOptions.hardware ? 1 : zoom})` }}
              onLoadedMetadata={() => {
                if (
                  streamRef.current &&
                  videoRef.current?.srcObject === streamRef.current
                )
                  setCameraReady(true);
              }}
              aria-label="Camera preview"
            />
          </div>
          {!cameraReady && !cameraError && (
            <p role="status" className="text-sm text-muted-foreground">
              Starting camera...
            </p>
          )}
          {cameraReady && !cameraError && (
            <div className="space-y-1">
              <div className="flex items-center justify-between text-sm">
                <span>Zoom {zoomOptions.hardware ? "" : "(digital)"}</span>
                <span aria-live="polite">{zoom.toFixed(1)}x</span>
              </div>
              <input
                type="range"
                aria-label="Camera zoom"
                className="h-10 w-full accent-[var(--primary)]"
                min={zoomOptions.min}
                max={zoomOptions.max}
                step={zoomOptions.step}
                value={zoom}
                disabled={capturing || zoomBusy}
                onChange={(event) => {
                  void changeZoom(Number(event.target.value));
                }}
              />
              {zoomError && (
                <p role="alert" className="text-sm text-destructive">
                  {zoomError}
                </p>
              )}
            </div>
          )}
          <DialogFooter className="flex-wrap gap-2">
            <Button type="button" variant="outline" onClick={closeCamera}>
              Cancel
            </Button>
            {cameraError ? (
              <>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    closeCamera();
                    galleryInputRef.current?.click();
                  }}
                >
                  Choose from Gallery
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    closeCamera();
                    cameraInputRef.current?.click();
                  }}
                >
                  Open device camera
                </Button>
              </>
            ) : (
              <Button
                type="button"
                disabled={!cameraReady || capturing || zoomBusy}
                onClick={capturePhoto}
              >
                {capturing ? "Preparing photo..." : "Capture photo"}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={reviewOpen}
        onOpenChange={(open) => {
          if (!open) handleCancel();
        }}
      >
        <DialogContent className="max-h-[calc(100dvh-2rem)] overflow-y-auto p-4 sm:max-w-2xl sm:p-6">
          <DialogHeader>
            <DialogTitle>Review selected files</DialogTitle>
            <DialogDescription>
              View your selections, remove wrong picks, or add more before
              uploading. {pendingFiles.length} file
              {pendingFiles.length === 1 ? "" : "s"} selected.
            </DialogDescription>
          </DialogHeader>

          {preparing ? (
            <p role="status" className="flex items-center gap-2 text-sm">
              <Loader2 className="h-4 w-4 animate-spin" />
              Preparing selected files...
            </p>
          ) : null}
          {selectionError ? (
            <p role="alert" className="text-sm text-destructive">
              {selectionError}
            </p>
          ) : null}
          <div className="flex flex-wrap gap-2">
            {showCameraOption && (
              <Button
                type="button"
                variant="outline"
                disabled={preparing || isUploading}
                onClick={() => openCamera(true)}
              >
                <Camera className="h-4 w-4" />
                {pendingFiles.length ? "Take another photo" : "Take photo"}
              </Button>
            )}
            <Button
              type="button"
              variant="outline"
              disabled={preparing || isUploading}
              onClick={() => galleryInputRef.current?.click()}
            >
              <Images className="h-4 w-4" />
              {pendingFiles.length
                ? multiple
                  ? "Add more files"
                  : "Replace file"
                : "Choose files again"}
            </Button>
          </div>
          {!preparing && pendingFiles.length === 0 && (
            <p className="rounded-lg border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
              No files selected.{" "}
              {showCameraOption
                ? "Take a photo or choose files to continue."
                : "Choose files to continue."}
            </p>
          )}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 max-h-[60vh] overflow-y-auto py-2">
            {pendingFiles.map((pending) => (
              <div key={pending.id} className="relative group">
                <button
                  type="button"
                  onClick={() => {
                    setFilePreview({ id: "", url: "" });
                    setViewingFile(pending);
                  }}
                  aria-label={`View ${pending.file.name}`}
                  className="relative flex aspect-square w-full items-center justify-center overflow-hidden rounded-lg border border-border bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  {pending.previewUrl ? (
                    <img
                      src={pending.previewUrl}
                      loading="lazy"
                      decoding="async"
                      alt={pending.file.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <FileText className="w-8 h-8 text-muted-foreground" />
                  )}
                  <span className="absolute bottom-1 right-1 flex items-center gap-1 rounded bg-black/70 px-2 py-1 text-xs text-white">
                    <Eye className="h-3 w-3" />
                    View
                  </span>
                </button>
                <button
                  type="button"
                  onClick={() => removePendingFile(pending.id)}
                  className="absolute top-1 right-1 h-8 w-8 rounded-full bg-destructive text-destructive-foreground flex items-center justify-center shadow-md hover:bg-destructive/90"
                  aria-label={`Remove ${pending.file.name}`}
                >
                  <X className="w-3.5 h-3.5" />
                </button>
                <p
                  className="mt-1 text-xs text-muted-foreground truncate"
                  title={pending.file.name}
                >
                  {pending.file.name}
                </p>
              </div>
            ))}
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={handleCancel}>
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleConfirmUpload}
              disabled={preparing || pendingFiles.length === 0}
            >
              <Upload className="w-4 h-4 mr-2" />
              Upload{pendingFiles.length > 0 ? ` (${pendingFiles.length})` : ""}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog
        open={Boolean(viewingFile)}
        onOpenChange={(open) => {
          if (!open) setViewingFile(null);
        }}
      >
        <DialogContent className="max-h-[calc(100dvh-2rem)] overflow-y-auto p-4 sm:max-w-4xl sm:p-6">
          <DialogHeader>
            <DialogTitle className="break-all pr-6">
              {viewingFile?.file.name || "File preview"}
            </DialogTitle>
            <DialogDescription>
              Review this file before uploading.
            </DialogDescription>
          </DialogHeader>
          {viewingFile && filePreview.id === viewingFile.id ? (
            viewingFile.file.type.startsWith("image/") ? (
              <img
                src={filePreview.url}
                alt={`Preview of ${viewingFile.file.name}`}
                className="max-h-[65vh] w-full rounded-lg object-contain"
              />
            ) : isPdfFile(viewingFile.file) ? (
              <iframe
                src={filePreview.url}
                title={`Preview of ${viewingFile.file.name}`}
                className="h-[60vh] w-full rounded-lg border border-border"
              />
            ) : (
              <p className="rounded-lg bg-muted p-6 text-sm text-muted-foreground">
                A preview is not available for this file type. Download it to
                view in your document app.
              </p>
            )
          ) : (
            <p role="status">Preparing preview...</p>
          )}
          <DialogFooter className="gap-2">
            {viewingFile && filePreview.id === viewingFile.id && (
              <Button variant="outline" asChild>
                <a href={filePreview.url} download={viewingFile.file.name}>
                  <Download className="h-4 w-4" />
                  Download to view
                </a>
              </Button>
            )}
            <Button
              type="button"
              variant="outline"
              onClick={() => setViewingFile(null)}
            >
              Back to selected files
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
